import React, { useContext } from 'react';
import { BaseRendererProps } from './eventRenderers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRepeat, faTurnUp, faClock } from '@fortawesome/free-solid-svg-icons';
import GameAssetsContext from '../../../../../../context/GameAssetContext';
import { getUniversalSourceIcon } from './StatChangeEvent';
import './RenderAggregatedStatChangeEvent.css';

// Mapping from stat variable names to display names
const statDisplayNames: Record<string, string> = {
  'attackSpeed': 'Attack Speed',
  'attackDamage': 'Attack Damage',
  'abilityPower': 'Ability Power',
  'armor': 'Armor',
  'magicResistance': 'Magic Resistance',
  'movementSpeed': 'Movement Speed',
  'resourceRegen': 'Resource Regeneration',
  'armorPenetrationPercent': 'Armor Penetration (%)',
  'lethality': 'Lethality',
  'magicPenetration': 'Magic Penetration',
  'magicPenetrationPercent': 'Magic Penetration (%)',
  'health': 'Health',
  'omnivamp': 'Omnivamp',
  'healEmpowered': 'Heal Empowered',
  'healShieldPower': 'Heal & Shield Power',
  'shield': 'Shield',
  'magicShield': 'Magic Shield',
  'physicalShield': 'Physical Shield',
  'critChance': 'Critical Strike Chance',
  'criticalStrikeChance': 'Critical Strike Chance',
  'manaRegen': 'Mana Regeneration',
  'healthRegen': 'Health Regeneration',
  'lifeSteal': 'Life Steal',
  'spellVamp': 'Spell Vamp',
  'physicalVamp': 'Physical Vamp',
  'tenacity': 'Tenacity',
  'abilityHaste': 'Ability Haste',
  'attackRange': 'Attack Range',
  'damageReduction': 'Damage Reduction',
  'physicalDamageReduction': 'Physical Damage Reduction',
  'magicDamageReduction': 'Magic Damage Reduction',
  'slowResist': 'Slow Resistance',
};

const RenderAggregatedStatChangeEvent: React.FC<BaseRendererProps> = ({ log }) => {
  const { 
    tickCount = 1, // Default to 1 if not provided
    statName, 
    changeAmount, 
    totalAmount, 
    changeType, 
    modifierType,
    source,
    statChangeOrigin,
    championName,
    sourceChampionName,
    isMaxStackRefresh = false,
    isRefresh = false,
    isTimeBasedSource = false,
    currentValue,
    maxValue,
    progressPercent
  } = log.details;

  // Debug logging
  console.log('[DEBUG] RenderAggregatedStatChangeEvent called with:', {
    logType: log.type,
    tickCount,
    isAggregated: tickCount > 1,
    statName,
    changeAmount,
    totalAmount,
    changeType,
    modifierType,
    details: log.details
  });

  const { gameWideAssets, championsAssets } = useContext(GameAssetsContext);
  const { shieldIcon, healEmpoweredIcon, damageReductionIcon, slowResistIcon } = gameWideAssets;

  const details = log.details;
  const sourceType = details.sourceType || statChangeOrigin || 'Unknown';
  const imageState = log.imageState;
  const abilityKey = details.spellKey;

  // Determine the stat icon based on statName - same logic as StatChangeEvent
  let statIcon = null;
  switch (statName) {
    case 'attackSpeed':
      statIcon = <div className="stat-icon attack-speed-icon"></div>;
      break;
    case 'attackDamage':
      statIcon = <div className="stat-icon attack-damage-icon"></div>;
      break;
    case 'abilityPower':
      statIcon = <div className="stat-icon ability-power-icon"></div>;
      break;
    case 'armor':
      statIcon = <div className="stat-icon armor-icon"></div>;
      break;
    case 'magicResistance':
      statIcon = <div className="stat-icon magic-resist-icon"></div>;
      break;
    case 'movementSpeed':
      statIcon = <div className="stat-icon movement-speed-icon"></div>;
      break;
    case 'resourceRegen':
      statIcon = <div className="stat-icon resource-regen-icon"></div>;
      break;
    case 'armorPenetrationPercent':
    case 'lethality':
      statIcon = <div className="stat-icon armor-penetration-icon"></div>;
      break;
    case 'magicPenetration':
    case 'magicPenetrationPercent':
      statIcon = <div className="stat-icon magic-penetration-icon"></div>;
      break;
    case 'health':
      statIcon = <div className="stat-icon health-icon"></div>;
      break;
    case 'omnivamp':
      statIcon = <div className="stat-icon omnivamp-icon"></div>;
      break;
    case 'gold':
      statIcon = <div className="stat-icon gold-icon"></div>;
      break;
    case 'abilityHaste':
      statIcon = <div className="stat-icon ability-haste-icon"></div>;
      break;
    case 'basicAbilityHaste':
      statIcon = <div className="stat-icon ability-haste-icon"></div>;
      break;
    case 'ultimateHaste':
      statIcon = <div className="stat-icon ability-haste-icon"></div>;
      break;
    case 'lifeSteal':
      statIcon = <div className="stat-icon life-steal-icon"></div>;
      break;
    case 'mana':
      statIcon = <div className="stat-icon resource-icon"></div>;
      break;
    case 'tenacity':
      statIcon = <div className="stat-icon tenacity-icon"></div>;
      break;
    case 'attackRange':
      statIcon = <div className="stat-icon attack-range-icon"></div>;
      break;
    case 'damageReduction':
      statIcon = (
        <div className="damage-mitigation-icon-container">
          <img
            src={damageReductionIcon}
            alt="Damage Reduction Icon"
            className="damage-mitigation-icon"
          />
          <span className="damage-reduction-arrow">↓</span>
        </div>
      );
      break;
      case 'physicalDamageReduction':
        statIcon = (
          <div className="damage-mitigation-icon-container">
            <img
              src={damageReductionIcon}
              alt="Physical Damage Reduction Icon"
              className="damage-mitigation-icon physical-damage-reduction"
            />
            <span className="damage-reduction-arrow">↓</span>
          </div>
        );
        break;
      case 'magicDamageReduction':
        statIcon = (
          <div className="damage-mitigation-icon-container">
            <img
              src={damageReductionIcon}
              alt="Magic Damage Reduction Icon"
              className="damage-mitigation-icon magic-damage-reduction"
            />
            <span className="damage-reduction-arrow">↓</span>
          </div>
        );
        break;
    case 'healEmpowered':
      statIcon = (
        <div className="shield-icon-container">
          <img
            src={healEmpoweredIcon}
            alt={`${statName} Icon`}
            className={`shield-icon-image ${statName}`}
          />
        </div>
      );
      break;
    case 'shield':
    case 'magicShield':
    case 'physicalShield':
      statIcon = (
        <div className="shield-icon-container">
          <img
            src={shieldIcon}
            alt={`${statName} Icon`}
            className={`shield-icon-image ${statName}`}
          />
        </div>
      );
      break;
    case 'critChance':
    case 'criticalStrikeChance':
      statIcon = <div className="stat-icon crit-chance-icon"></div>;
      break;
    case 'slowResist':
      statIcon = <img src={slowResistIcon} alt="Slow Resistance Icon" className="stat-icon slow-resist-icon" />;
      break;
    case 'healShieldPower':
      statIcon = <div className="stat-icon heal-shield-power-icon"></div>;
      break;
    default:
      statIcon = <div className="stat-icon default-stat-icon"></div>;
  }

  // Use the universal source icon from StatChangeEvent
  // Use sourceChampionName if available and different from target championName
  const imageChampionName = sourceChampionName && sourceChampionName !== championName ? sourceChampionName : championName;
  
  const sourceIcon = getUniversalSourceIcon(
    {
      sourceType: sourceType,
      imageState: imageState,
      sourceName: source,
      championName: imageChampionName,
      abilityKey: abilityKey,
    },
    gameWideAssets,
    championsAssets
  );

  // Determine the sign and display amount - same logic as StatChangeEvent
  const isDamageReduction = statName === 'damageReduction' || statName === 'physicalDamageReduction' || statName === 'magicDamageReduction';
  const sign = changeType === 'increase' ? '+' : '-';
  const isAggregated = tickCount > 1;
  
  // Check if we should show decimal values and handle undefined values
  const showDecimal = details.showDecimalValue === true;
  const safeChangeAmount = changeAmount ?? 0;
  const safeTotalAmount = totalAmount ?? 0;
  
  const displayIncrementalAmount = showDecimal ? 
    Math.abs(safeChangeAmount).toString() : 
    Math.abs(safeChangeAmount).toFixed(0);
  const displayTotalAmount = showDecimal ? 
    Math.abs(safeTotalAmount).toString() : 
    Math.abs(safeTotalAmount).toFixed(0);
  const unit = modifierType === 'percent' ? '%' : '';
  
  // Determine signs based on changeType
  const incrementalSign = changeType === 'increase' ? '+' : '-';
  // For total amount, use the same logic - if it's a decrease effect, total should be negative
  const totalSign = changeType === 'increase' ? '+' : '-';
  
  // Check if this is a max stack refresh event (duration refresh with no stat change)
  const isMaxStackRefreshEvent = isMaxStackRefresh && safeChangeAmount === 0;

  // Decide what to display based on whether this is a stacking source, resource usage, time-based, and changeAmount
  let amountDisplay = '';
  let totalDisplay = '';
  let timeDurationDisplay = '';
  
  // Check if this is from a stacking source (like Conqueror, Ezreal passive, etc.)
  const isStackingSource = details.isStackingSource;
  
  // Check if this is a resource usage event (ability cost)
  const isResourceUsage = modifierType === 'ResourceUsage';
  
  // Calculate elapsed time for time-based sources (tickCount * 0.25s per tick, assuming 0.25s intervals)
  const elapsedTime = isTimeBasedSource ? (tickCount * 0.25).toFixed(2) : null;
  
  if (isTimeBasedSource) {
    // For time-based sources, show current total value with progress indicator
    const safeCurrentValue = currentValue ?? totalAmount ?? 0;
    const safeMaxValue = maxValue ?? 0;
    const displayCurrentValue = showDecimal ? 
      Math.abs(safeCurrentValue).toString() : 
      Math.abs(safeCurrentValue).toFixed(1);
    
    amountDisplay = `${totalSign}${displayCurrentValue}${unit}`;
    timeDurationDisplay = `${elapsedTime}s`;
    
    // Show progress if we have max value
    if (safeMaxValue > 0 && safeCurrentValue < safeMaxValue) {
      const progress = ((safeCurrentValue / safeMaxValue) * 100).toFixed(0);
      totalDisplay = `(${progress}%)`;
    }
  } else if (isResourceUsage) {
    // For resource usage, show the amount used and percentage
    const percentageUsed = details.percentageChange ? (details.percentageChange * 100).toFixed(1) : '0';
    amountDisplay = `${incrementalSign}${displayIncrementalAmount}`;
    totalDisplay = `(${percentageUsed}%)`;
  } else if (isStackingSource) {
    if (safeChangeAmount !== 0) {
      // Show total amount on first line, incremental in brackets on second line
      amountDisplay = `${totalSign}${displayTotalAmount}${unit}`;
      totalDisplay = `(${incrementalSign}${displayIncrementalAmount}${unit})`;
    } else if (isMaxStackRefreshEvent) {
      // For max stack refresh events, show total amount with incremental inline
      amountDisplay = `${totalSign}${displayTotalAmount}${unit} `;
      totalDisplay = `(${incrementalSign}0${unit})`; // Will be shown inline for max stack refresh
    } else {
      // Show total with no incremental change
      amountDisplay = `${totalSign}${displayTotalAmount}${unit}`;
      totalDisplay = `(${incrementalSign}0${unit})`;
    }
  } else {
    if (safeChangeAmount !== 0) {
      // Show only incremental amount for other stats
      amountDisplay = `${incrementalSign}${displayIncrementalAmount}${unit}`;
    } else {
      // Optionally handle zero change amount
      amountDisplay = `${statName} unchanged`;
    }
  }

  // Get the display name for the stat
  const statDisplayName = statDisplayNames[statName] || statName;

  // Create detailed tooltip based on what values we're actually showing
  let tooltipText = '';
  
  if (isTimeBasedSource) {
    // Special handling for time-based sources
    const safeCurrentValue = currentValue ?? totalAmount ?? 0;
    const safeMaxValue = maxValue ?? 0;
    const displayCurrentValue = Math.abs(safeCurrentValue).toFixed(1);
    const displayMaxValue = Math.abs(safeMaxValue).toFixed(1);
    const progress = safeMaxValue > 0 ? ((safeCurrentValue / safeMaxValue) * 100).toFixed(0) : '100';
    
    if (safeMaxValue > 0 && safeCurrentValue < safeMaxValue) {
      tooltipText = `${statDisplayName}: ${totalSign}${displayCurrentValue}${unit} / ${displayMaxValue}${unit} (${progress}%)\nIncreasing over time (${elapsedTime}s elapsed)\nIncremental change: ${incrementalSign}${displayIncrementalAmount}${unit}`;
    } else if (safeMaxValue > 0) {
      tooltipText = `${statDisplayName}: ${totalSign}${displayCurrentValue}${unit} (Maximum reached)\nTotal time: ${elapsedTime}s\nIncremental change: ${incrementalSign}${displayIncrementalAmount}${unit}`;
    } else {
      tooltipText = `${statDisplayName}: ${totalSign}${displayCurrentValue}${unit}\nIncreasing over time (${elapsedTime}s elapsed)\nIncremental change: ${incrementalSign}${displayIncrementalAmount}${unit}`;
    }
  } else if (isRefresh) {
    // Special handling for refresh events
    if (isAggregated) {
      tooltipText = `${statDisplayName}: ${incrementalSign}${displayIncrementalAmount}${unit}\nDuration refreshed ${tickCount} times (actual stat change: 0)`;
    } else {
      tooltipText = `${statDisplayName}: ${incrementalSign}${displayIncrementalAmount}${unit}\nDuration refreshed (actual stat change: 0)`;
    }
  } else if (isResourceUsage) {
    // For resource usage events, show "Ability Cost" in tooltip
    const percentageUsed = details.percentageChange ? (details.percentageChange * 100).toFixed(1) : '0';
    if (isAggregated) {
      tooltipText = `Ability Cost: ${displayIncrementalAmount} (${percentageUsed}% of total resources) - reapplied ${tickCount} times`;
    } else {
      tooltipText = `Ability Cost: ${displayIncrementalAmount} (${percentageUsed}% of total resources)`;
    }
  } else if (isStackingSource) {
    if (safeChangeAmount !== 0) {
      // Stacking sources show total first, then incremental in brackets
      if (isAggregated) {
        tooltipText = `Total ${statDisplayName} Change: ${totalSign}${displayTotalAmount}${unit}\nAdditional ${statDisplayName}: ${incrementalSign}${displayIncrementalAmount}${unit} (reapplied ${tickCount} times, refreshing duration)`;
      } else {
        tooltipText = `Total ${statDisplayName} Change: ${totalSign}${displayTotalAmount}${unit}\nAdditional ${statDisplayName}: ${incrementalSign}${displayIncrementalAmount}${unit}`;
      }
    } else {
      // Zero incremental change - handle max stack refresh differently
      if (isMaxStackRefreshEvent) {
        tooltipText = `${statDisplayName}: ${totalSign}${displayTotalAmount}${unit} (max stacks reached, duration refreshed ${tickCount} times)`;
      } else if (isAggregated) {
        tooltipText = `Total ${statDisplayName} Change: ${totalSign}${displayTotalAmount}${unit}\nNo additional change this instance (reapplied ${tickCount} times)`;
      } else {
        tooltipText = `Total ${statDisplayName} Change: ${totalSign}${displayTotalAmount}${unit}\nNo additional change this instance`;
      }
    }
  } else if (safeChangeAmount !== 0) {
    // Other stats only show incremental, so only show incremental in tooltip
    if (isAggregated) {
      tooltipText = `${statDisplayName}: ${incrementalSign}${displayIncrementalAmount}${unit} (reapplied ${tickCount} times, refreshing duration)`;
    } else {
      tooltipText = `${statDisplayName}: ${incrementalSign}${displayIncrementalAmount}${unit}`;
    }
  } else {
    // Zero change amount case
    if (isAggregated) {
      tooltipText = `Total ${statDisplayName}: ${totalSign}${displayTotalAmount}${unit} (reapplied ${tickCount} times)`;
    } else {
      tooltipText = `Total ${statDisplayName}: ${totalSign}${displayTotalAmount}${unit}`;
    }
  }

  return (
    <div className={`stat-change-event ${isTimeBasedSource ? 'time-based-stat' : ''}`} title={tooltipText}>
      <div className="stat-change-amount-wrapper">
        {/* Stat Icon with repeat indicator underneath for aggregated events */}
        <div className="stat-icon-with-repeat">
          {statIcon}
        </div>
        
        <div className="stat-change-amounts">
          <div className="stat-amount-row">
            <span className="stat-change-amount">
              {amountDisplay}
              {isMaxStackRefreshEvent && totalDisplay && (
                <span className="stat-change-total-inline">{totalDisplay}</span>
              )}
            </span>
            {/* Show clock icon and time for time-based sources */}
            {isTimeBasedSource && timeDurationDisplay && (
              <div className="stat-time-badge">
                <FontAwesomeIcon icon={faClock} className="stat-time-icon" />
                <span className="stat-time-duration">{timeDurationDisplay}</span>
              </div>
            )}
            {/* Show repeat badge for aggregated non-time-based events */}
            {isAggregated && !isMaxStackRefreshEvent && !isTimeBasedSource && (
              <div className="stat-repeat-badge">
                <FontAwesomeIcon icon={faRepeat} className="stat-repeat-icon" />
                <span className="stat-repeat-count">{tickCount}</span>
              </div>
            )}
            {isRefresh && !isAggregated && !isTimeBasedSource && (
              <div className="stat-refresh-badge">
                <FontAwesomeIcon icon={faRepeat} className="stat-refresh-icon" />
              </div>
            )}
          </div>
          {!isMaxStackRefreshEvent && totalDisplay && <span className="stat-change-total">{totalDisplay}</span>}
          {isMaxStackRefreshEvent && isAggregated && (
            <div className="stat-repeat-badge">
              <FontAwesomeIcon icon={faRepeat} className="stat-repeat-icon" />
              <span className="stat-repeat-count">{tickCount}</span>
            </div>
          )}
        </div>
      </div>
      
      {sourceIcon && (
        <div className="stat-change-source-wrapper">
          <FontAwesomeIcon icon={faTurnUp} className="fa-turn-up" />
          {sourceIcon}
        </div>
      )}
    </div>
  );
};

export default RenderAggregatedStatChangeEvent; 